import { useEffect, useState } from 'react'
import { firstValueFrom } from 'rxjs'
import { useSelector } from '../../../Flux'
import type { State } from '../../../Flux/Slice'
import type { IOrder } from '../../../Model/index.js'
import { getOrder } from '../../../Repository/Order/OrderRepository.js'
import { getProject } from '../../../Repository/Project/ProjectRepository.js'
import { isNotFoundError } from '../../Component/scopeNotFound.ts'

/** `order` is the owning order once fetched (null for a project, or when its fetch failed other than with a 404). */
export type ScopeOwner = { status: 'loading' } | { status: 'notFound' } | { status: 'found'; order: IOrder | null }

const FOUND_UNKNOWN: ScopeOwner = { status: 'found', order: null }

/**
 * Whether the project or order owning a scope exists for the user. The viz endpoints answer a foreign scope with empty
 * data, so only the owner tells "not found or no access" from "no data yet". Taken from Redux when it holds this
 * owner, fetched otherwise. A failure other than a 404 counts as found, leaving the data views to show their errors.
 */
export function useScopeOwner(type: 'PROJECT' | 'ORDER', id: string | undefined, enabled: boolean): ScopeOwner {
  const reduxOrder = useSelector((s: typeof State) => (type === 'ORDER' ? s.order?.order : undefined))
  const reduxProjectId = useSelector((s: typeof State) => (type === 'PROJECT' ? s.project?.currentProject?.id : undefined))
  const orderInRedux = reduxOrder && reduxOrder !== 'LOADING' && reduxOrder.id === id ? reduxOrder : null
  const inRedux = orderInRedux !== null || (reduxProjectId !== undefined && reduxProjectId === id)
  const needsFetch = enabled && !!id && !inRedux

  const [fetched, setFetched] = useState<{ id: string; owner: ScopeOwner } | null>(null)
  useEffect(() => {
    if (!needsFetch || !id) {
      return
    }
    let cancelled = false
    const owner: Promise<IOrder | null> = type === 'ORDER' ? firstValueFrom(getOrder(id)) : firstValueFrom(getProject(id)).then(() => null)
    owner
      .then((order) => {
        if (!cancelled) {
          setFetched({ id, owner: { status: 'found', order } })
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setFetched({ id, owner: isNotFoundError(e) ? { status: 'notFound' } : FOUND_UNKNOWN })
        }
      })
    return () => {
      cancelled = true
    }
  }, [needsFetch, type, id])

  if (!enabled || !id) {
    return FOUND_UNKNOWN
  }
  if (inRedux) {
    return { status: 'found', order: orderInRedux }
  }
  return fetched?.id === id ? fetched.owner : { status: 'loading' }
}
